<script lang="ts" setup>
	defineProps<{
		title?: string
	}>()

	const { data: profile, status: profileStatus } = useLazyDataResolver('profile')
</script>

<template>
	<div class="overflow-x-hidden">
		<Head>
			<Title v-if="title">{{ title }}</Title>
		</Head>

		<slot />

		<slot name="footer">
			<GsPageSection as="div" class="pt-12 pb-4 bg-primary text-white">
				<div class="flex flex-col justify-baseline gap-2.5">
					<NewsletterSubscription />
					<hr class="border-white/10" />
					<div class="grid md:grid-cols-2 lg:grid-cols-4 gap-10 my-10 lg:my-12">
						<div class="flex flex-col md:flex-row gap-4 lg:flex-col">
							<div class="text-center md:text-left flex-none">
								<div class="bg-white p-1 rounded-sm inline-block">
									<GsImg
										:src="usePublicUrl('~/assets/img/Logo-Ludwig-Pfeiffer.svg')"
										class="h-16 lg:h-21.75 w-auto relative"
										fit="contain"
										alt="Logo der Ludwig Pfeiffer Hoch- und Tiefbau GmbH & Co. KG"
										width="128" height="114"
									/>
								</div>
							</div>
							<p class="text-center md:text-left text-[1rem] text-white/50 2xl:w-[80%]">Ihr zuverlässiger Partner für professionelle Tiefbau- und Rohrleitungsbauprojekte.</p>
						</div>
						<template v-if="profile?.items.length === 1">
							<div v-for="(item, index) in profile?.items[0].footer_link_groups_display" :key="index" class="flex xl:justify-end">
								<div class="flex flex-col w-full h-full">
									<span class="block text-[1.25rem] text-white font-bold mb-1.5 lg:mb-2 text-center md:text-left">{{ item.title }}</span>
									<ul class="flex flex-col gap-1.5 lg:gap-2 mb-4 lg:mb-0 [&_li]:text-center md:[&_li]:text-left">
										<li v-for="(link, linkIndex) in item.items" :key="linkIndex">
											<NuxtLink v-if="link.url.startsWith('http')" :to="link.url" class="text-[1rem] text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>{{ link.title }}</NuxtLink>
											<NuxtLink v-else :to="{name: 'article-detail', params: { 'article_slug': link.url }}" class="text-[1rem] text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>{{ link.title }}</NuxtLink>
										</li>
									</ul>
									<div v-if="index === profile.items[0].footer_link_groups_display.length - 1" class="flex md:inline mx-auto md:mx-0 mt-12 md:mt-6">
										<ProfileSocials v-if="profile.items[0].social_links.length > 0" :socials="profile.items[0].social_links" />
									</div>
								</div>
							</div>
						</template>
					</div>
					<hr class="border-white/10" />
					<div class="flex flex-col lg:flex-row lg:justify-between">
						<p v-if="profile?.items.length === 1" class="text-[1rem] text-white/50 text-center lg:text-left">
							{{ profile.items[0].footer_claim }}
						</p>
						<nav aria-label="Footer Navigation" class="relate -translate-x-1/2">
							<ul class="flex flex-col lg:flex-row gap-4 lg:gap-5">
								<li><NuxtLink :to="{ name: 'article-detail', params: { 'article_slug': 'impressum' }}" class="text-[1rem] text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>Impressum</NuxtLink></li>
								<li><NuxtLink :to="{ name: 'article-detail', params: { 'article_slug': 'datenschutz' }}" class="text-[1rem] text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>Datenschutz</NuxtLink></li>
								<li><NuxtLink :to="{ name: 'article-detail', params: { 'article_slug': 'allgemeine-geschaeftsbedingungen' }}" class="text-[1rem] text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>AGB</NuxtLink></li>
								<li>
									<NuxtLink :to="{ name: 'intranet:dashboard' }" class="text-[1rem] inline-flex *:my-auto text-white/50 hover:text-white transition-all duration-300 ease-in-out" no-prefetch>
										<GsIcon name="material-symbols:lock-outline-sharp" class="mr-1.5 size-3.5 transition duration-300 ease-in-out"></GsIcon>Intranet
									</NuxtLink>
								</li>
							</ul>
						</nav>
						<p>
							<NuxtLink to="https://www.markov-markov.de" target="_blank" class="text-sm md:text-[1rem] lg:text-[1.25rem] text-white hover:text-accent flex justify-center transition-all duration-300 ease-in-out" title="markov&markov Agentur-Website" external no-prefetch>
								<GsIcon name="fa:gears" class="size-4 xl:size-5.5 my-auto mr-2" />
								markov&markov
							</NuxtLink>
						</p>
					</div>
				</div>
			</GsPageSection>
		</slot>
	</div>
</template>

<style lang="postcss" global>
	main {
		overflow: hidden
	}
	.page-enter-active,
	.page-leave-active {
		transition: all 0.15s;
	}
	.page-enter-from,
	.page-leave-to {
		opacity: 0;
		transform: scale(0.9);
		filter: blur(.5rem);
	}
</style>
